import { useEffect, useState } from 'react';
import type { AppRole, AppUser, UserStatus } from '@cer-autofill/shared';
import {
  apiErrorReason,
  createUser,
  deleteUser,
  fetchAudit,
  fetchUsers,
  updateUser,
} from '../api.js';

const ROLE_OPTIONS: AppRole[] = ['admin', 'qa', 'support', 'inspector'];

export function UserManagementScreen({ embedded = false }: { embedded?: boolean }) {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [status, setStatus] = useState<UserStatus | ''>('');
  const [role, setRole] = useState<AppRole | ''>('');
  const [auditCount, setAuditCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    email: '',
    displayName: '',
    legalName: '',
    mobile: '',
    cerRegistrationNumber: '',
    inspectorId: '',
    roles: ['admin'] as AppRole[],
  });
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [nextUsers, audit] = await Promise.all([
        fetchUsers({
          ...(status ? { status } : {}),
          ...(role ? { role } : {}),
          limit: 200,
        }),
        fetchAudit(50),
      ]);
      setUsers(nextUsers);
      setAuditCount(audit.length);
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [status, role]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const details = {
        email: form.email,
        displayName: form.displayName,
        roles: form.roles,
        ...(form.legalName ? { legalName: form.legalName } : {}),
        ...(form.mobile ? { mobile: form.mobile } : {}),
        ...(form.cerRegistrationNumber
          ? { cerRegistrationNumber: form.cerRegistrationNumber }
          : {}),
        ...(form.inspectorId ? { inspectorId: form.inspectorId } : {}),
      };
      if (editingUserId) await updateUser(editingUserId, details);
      else await createUser({ ...details, status: 'active' });
      resetForm();
      await load();
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  }

  function resetForm() {
    setEditingUserId(null);
    setForm({
      email: '',
      displayName: '',
      legalName: '',
      mobile: '',
      cerRegistrationNumber: '',
      inspectorId: '',
      roles: ['admin'],
    });
  }

  function beginEdit(user: AppUser) {
    setEditingUserId(user.userId);
    setForm({
      email: user.email,
      displayName: user.displayName,
      legalName: user.legalName ?? '',
      mobile: user.mobile ?? '',
      cerRegistrationNumber: user.cerRegistrationNumber ?? '',
      inspectorId: user.inspectorId ?? '',
      roles: [...user.roles],
    });
  }

  async function onDelete(user: AppUser) {
    if (!window.confirm(`Delete ${user.displayName} and revoke their sign-in?`)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteUser(user.userId);
      if (editingUserId === user.userId) resetForm();
      await load();
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  }

  async function onToggleStatus(user: AppUser) {
    setSaving(true);
    setError(null);
    try {
      await updateUser(user.userId, {
        status: user.status === 'active' ? 'disabled' : 'active',
      });
      await load();
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  }

  async function onRoleToggle(user: AppUser, changedRole: AppRole) {
    const nextRoles = user.roles.includes(changedRole)
      ? user.roles.filter((r) => r !== changedRole)
      : [...user.roles, changedRole];
    if (nextRoles.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await updateUser(user.userId, { roles: nextRoles });
      await load();
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  }

  function toggleFormRole(roleName: AppRole) {
    setForm((current) => {
      const roles = current.roles.includes(roleName)
        ? current.roles.filter((r) => r !== roleName)
        : [...current.roles, roleName];
      return { ...current, roles: roles.length > 0 ? roles : current.roles };
    });
  }

  return (
    <div className={embedded ? '' : 'screen'}>
      {!embedded && (
        <div className="screen__header">
          <div>
            <h2>Admin</h2>
            <p className="subtitle">Cognito accounts, application roles, and audit history.</p>
          </div>
          <button type="button" onClick={() => void load()} disabled={loading || saving}>
            Refresh
          </button>
        </div>
      )}

      {error && <p className="error">{error}</p>}

      <section className="admin-grid">
        <div className="card">
          <h3>{editingUserId ? 'Edit user' : 'Add user'}</h3>
          <p className="muted">
            Creates a real sign-in account. The user can then request an email sign-in code.
          </p>
          <form className="admin-user-form" onSubmit={(e) => void onSave(e)}>
            <label>
              <span>Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
            </label>
            <label>
              <span>Display name</span>
              <input
                type="text"
                value={form.displayName}
                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                required
              />
            </label>
            {form.roles.includes('inspector') && (
              <>
                <label>
                  <span>Actual / PDF name</span>
                  <input
                    value={form.legalName}
                    onChange={(e) => setForm((f) => ({ ...f, legalName: e.target.value }))}
                    required
                  />
                </label>
                <label>
                  <span>Inspector key</span>
                  <input
                    value={form.inspectorId}
                    onChange={(e) => setForm((f) => ({ ...f, inspectorId: e.target.value }))}
                    required
                  />
                </label>
                <label>
                  <span>Mobile</span>
                  <input
                    value={form.mobile}
                    onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
                  />
                </label>
                <label>
                  <span>CER registration number</span>
                  <input
                    value={form.cerRegistrationNumber}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, cerRegistrationNumber: e.target.value }))
                    }
                    required
                  />
                </label>
              </>
            )}
            <div>
              <span className="field-label">Roles</span>
              <div className="role-pills">
                {ROLE_OPTIONS.map((r) => (
                  <label key={r} className="role-pill">
                    <input
                      type="checkbox"
                      checked={form.roles.includes(r)}
                      onChange={() => toggleFormRole(r)}
                    />
                    {r}
                  </label>
                ))}
              </div>
            </div>
            <div className="modal__actions">
              {editingUserId && (
                <button type="button" onClick={resetForm}>
                  Cancel
                </button>
              )}
              <button type="submit" className="primary" disabled={saving}>
                {editingUserId ? 'Save user' : 'Create sign-in user'}
              </button>
            </div>
          </form>
        </div>

        <div className="card">
          <h3>Filters</h3>
          <div className="filters-grid">
            <label>
              <span>Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as UserStatus | '')}>
                <option value="">Any</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
            <label>
              <span>Role</span>
              <select value={role} onChange={(e) => setRole(e.target.value as AppRole | '')}>
                <option value="">Any</option>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="muted">{auditCount} recent audit events available.</p>
        </div>
      </section>

      {loading ? (
        <p className="loading">Loading users…</p>
      ) : users.length === 0 ? (
        <p className="empty">No users match these filters.</p>
      ) : (
        <div className="users-table" role="table">
          <div className="users-table__row users-table__row--header" role="row">
            <div>Email</div>
            <div>Name</div>
            <div>Roles</div>
            <div>Status</div>
            <div>Updated</div>
            <div>Actions</div>
          </div>
          {users.map((user) => (
            <div key={user.userId} className="users-table__row" role="row">
              <div className="mono">{user.email}</div>
              <div>{user.displayName}</div>
              <div className="role-pills role-pills--compact">
                {ROLE_OPTIONS.map((r) => (
                  <label key={r} className="role-pill">
                    <input
                      type="checkbox"
                      checked={user.roles.includes(r)}
                      onChange={() => void onRoleToggle(user, r)}
                      disabled={saving}
                    />
                    {r}
                  </label>
                ))}
              </div>
              <div>
                <span className={`pill pill--${user.status}`}>{user.status}</span>
              </div>
              <div>{new Date(user.updatedAt).toLocaleString()}</div>
              <div>
                <button type="button" onClick={() => beginEdit(user)} disabled={saving}>
                  Edit
                </button>{' '}
                <button type="button" onClick={() => void onToggleStatus(user)} disabled={saving}>
                  {user.status === 'active' ? 'Disable' : 'Reactivate'}
                </button>{' '}
                <button
                  type="button"
                  className="danger"
                  onClick={() => void onDelete(user)}
                  disabled={saving}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
